import { homeViewFor, USER_ROLE_LABELS, type Actor } from '@blueprint/domain'
import { useEffect, useState, type MouseEvent } from 'react'
import { Button } from '../components/controls.js'
import controls from '../components/controls.module.css'
import { SupportView } from '../features/admin/SupportView.js'
import { UsersView } from '../features/admin/UsersView.js'
import { AppearanceDialog } from '../features/appearance/AppearanceDialog.js'
import { AccountSettingsDialog, ProfileDialog } from '../features/auth/ProfileDialog.js'
import { SignInView } from '../features/auth/SignInView.js'
import { ShareBoardDialog } from '../features/boards/ShareBoardDialog.js'
import { ProjectDialogs } from '../features/boards/ProjectDialogs.js'
import { BoardNameDialog } from '../features/boards/BoardNameDialog.js'
import { BoardSwitcherDialog } from '../features/boards/BoardSwitcherDialog.js'
import { BoardsView } from '../features/boards/BoardsView.js'
import { ConfirmDialog } from '../features/boards/ConfirmDialog.js'
import { ImportDialog } from '../features/boards/ImportDialog.js'
import { HistoricalBoardView } from '../features/boards/HistoricalBoardView.js'
import { SaveFailureBanner } from '../features/boards/SaveStatus.js'
import { BoardCardPromptDialog, BoardScopePromptDialog } from '../features/cards/BoardCardPromptDialog.js'
import { ScopeContextDialog } from '../features/context/ScopeContextDialog.js'
import { ReconciliationDialog } from '../features/findings/Findings.js'
import { CardEditorDialog } from '../features/cards/CardEditorDialog.js'
import { MoveCardDialog } from '../features/cards/MoveCardDialog.js'
import { DragVirtualPrototype } from '../features/prototype/DragVirtualPrototype.js'
import { Minimap } from '../features/story-map/Minimap.js'
import { MatchSetContext } from '../features/story-map/filter-context.js'
import { StoryMap } from '../features/story-map/StoryMap.js'
import { MindmapView } from '../features/mind-map/MindmapView.js'
import { WorkflowView } from '../features/workflow/WorkflowView.js'
import { HeadingNotesDialog } from '../features/heading-notes/HeadingNotes.js'
import { hasUnsavedMindmap } from '../state/mindmap-store.js'
import { hasUnsavedWorkflow } from '../state/workflow-store.js'
import { hasUnsavedWork, useBoardStore } from '../state/board-store.js'
import { useFilterIndex } from '../state/filter-index.js'
import { sessionRepository, useSessionStore } from '../state/session-store.js'
import { useUiStore } from '../state/ui-store.js'
import { FooterBar } from './FooterBar.js'
import { TopBar } from './TopBar.js'
import styles from './App.module.css'

function isTextEntry(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  const tag = target.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable
}

export function App() {
  // A tiny hash route keeps the drag/virtualization harness reachable without a router.
  const [isPrototype, setIsPrototype] = useState(() => window.location.hash === '#prototype')
  useEffect(() => {
    const onHashChange = () => setIsPrototype(window.location.hash === '#prototype')
    window.addEventListener('hashchange', onHashChange)
    return () => window.removeEventListener('hashchange', onHashChange)
  }, [])

  if (isPrototype) return <DragVirtualPrototype />
  return <AuthenticatedApp />
}

/**
 * Nothing loads until we know who is asking. A signed-out browser holds no board data at
 * all, and platform staff never reach the board application: tenants and feedback are
 * administered at Teamcentric Accounts.
 */
function AuthenticatedApp() {
  const phase = useSessionStore((state) => state.phase)
  const user = useSessionStore((state) => state.user)
  const check = useSessionStore((state) => state.check)
  const backToAccountsUrl = useSessionStore((state) => state.backToAccountsUrl)

  useEffect(() => {
    void check()
  }, [check])

  if (phase === 'checking') return <div className={styles.state} aria-busy="true" />
  if (phase === 'signed-out' || !user) return <SignInView />
  const page = homeViewFor(user) === 'support' ? <SupportView /> : <BoardApp />
  if (!backToAccountsUrl) return page
  // Switch as: the banner sits above every page.
  return (
    <div className={styles.switched}>
      <SwitchedBanner user={user} url={backToAccountsUrl} />
      <div className={styles.switchedPage}>{page}</div>
    </div>
  )
}

/** An Accounts super admin viewing Blueprint as a demo user (Switch as). Back to Accounts ends the session. */
function SwitchedBanner({ user, url }: { user: Actor; url: string }) {
  const backToAccounts = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault()
    void sessionRepository().logOut().catch(() => null).finally(() => window.location.assign(url))
  }
  return (
    <div className={styles.switchedBanner} role="status">
      <span>Viewing as {user.displayName} ({USER_ROLE_LABELS[user.role]}) —</span>
      <a className={controls.button} href={url} onClick={backToAccounts}>Back to Accounts</a>
    </div>
  )
}

function BoardApp() {
  const phase = useBoardStore((state) => state.phase)
  const activeBoardId = useBoardStore((state) => state.activeBoardId)
  const viewedVersion = useBoardStore((state) => state.viewedVersion)
  const saveStatus = useBoardStore((state) => state.save.status)
  const syncRemote = useBoardStore((state) => state.syncRemote)
  const error = useBoardStore((state) => state.error)
  const initialise = useBoardStore((state) => state.initialise)
  const dismissError = useBoardStore((state) => state.dismissError)
  const filterIndex = useFilterIndex()
  const view = useUiStore((state) => state.view)
  const usersOpen = useUiStore((state) => state.usersOpen)
  const editorOpen = useUiStore((state) => Boolean(state.editor))
  const setUsersOpen = useUiStore((state) => state.setUsersOpen)

  useEffect(() => {
    void initialise()
  }, [initialise])

  useEffect(() => {
    if (view !== 'board' || usersOpen || phase !== 'ready' || !activeBoardId || viewedVersion) return
    let checking = false
    let checkAgain = false
    let stream: EventSource | null = null
    let fallback: number | null = null
    const check = () => {
      if (document.visibilityState === 'hidden') return
      if (checking) {
        checkAgain = true
        return
      }
      checking = true
      void syncRemote().finally(() => {
        checking = false
        if (checkAgain) {
          checkAgain = false
          check()
        }
      })
    }
    const startFallback = () => {
      if (document.visibilityState === 'hidden') return
      if (fallback === null) fallback = window.setInterval(check, 15_000)
    }
    const stopFallback = () => {
      if (fallback !== null) window.clearInterval(fallback)
      fallback = null
    }
    const connect = () => {
      if (document.visibilityState === 'hidden' || stream) return
      if (typeof EventSource === 'undefined') {
        startFallback()
        return
      }
      stream = new EventSource(`/api/boards/${encodeURIComponent(activeBoardId)}/events`)
      startFallback() // Covers a slow initial connection as well as a reconnect.
      stream.addEventListener('revision', check)
      stream.onopen = () => {
        stopFallback()
        check() // Catch a change committed before the stream was established.
      }
      stream.onerror = () => {
        // EventSource reconnects itself; keep a slow revision poll while it is down.
        startFallback()
      }
    }
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        stream?.close()
        stream = null
        stopFallback()
      } else {
        connect()
        check()
      }
    }
    connect()
    document.addEventListener('visibilitychange', onVisibilityChange)
    window.addEventListener('focus', check)
    return () => {
      stream?.close()
      stopFallback()
      document.removeEventListener('visibilitychange', onVisibilityChange)
      window.removeEventListener('focus', check)
    }
  }, [activeBoardId, phase, syncRemote, usersOpen, view, viewedVersion])

  // A notification received while a local save or card edit was in progress was
  // deliberately deferred by syncRemote. Recheck when it is safe to load it.
  useEffect(() => {
    if (saveStatus === 'saved' && !editorOpen && activeBoardId && view === 'board'
      && phase === 'ready' && !viewedVersion && document.visibilityState !== 'hidden') {
      void syncRemote()
    }
  }, [activeBoardId, editorOpen, phase, saveStatus, syncRemote, view, viewedVersion])

  // Ctrl/Cmd+Z and Ctrl/Cmd+Shift+Z, but never while typing.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!(event.ctrlKey || event.metaKey) || event.key.toLowerCase() !== 'z') return
      if (isTextEntry(event.target)) return
      if (useUiStore.getState().editor || useUiStore.getState().confirm) return
      event.preventDefault()
      if (event.shiftKey) useBoardStore.getState().redo()
      else useBoardStore.getState().undo()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  // Best-effort warning only: browsers do not guarantee unload handlers run.
  useEffect(() => {
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!hasUnsavedWork() && !hasUnsavedMindmap() && !hasUnsavedWorkflow()) return
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [])

  return (
    <MatchSetContext.Provider value={filterIndex.matchSet}>
      {usersOpen ? (
        <UsersView onClose={() => setUsersOpen(false)} />
      ) : view === 'boards' ? (
        <BoardsView />
      ) : view === 'mindmap' ? (
        <MindmapView />
      ) : view === 'workflow' ? (
        <WorkflowView />
      ) : (
        <div className={styles.shell}>
          <TopBar filterIndex={filterIndex} />
          {error ? (
            <div className={styles.errorBar} role="alert">
              <span className={styles.errorText}>{error}</span>
              <Button onClick={dismissError}>Dismiss</Button>
            </div>
          ) : null}
          <SaveFailureBanner />
          <BoardArea phase={phase} />
          <FooterBar />
        </div>
      )}

      {view === 'board' && !viewedVersion ? <Minimap /> : null}

      <CardEditorDialog />
      <BoardCardPromptDialog />
      <BoardScopePromptDialog />
      <ScopeContextDialog />
      <HeadingNotesDialog />
      <ReconciliationDialog />
      <MoveCardDialog />
      <ImportDialog />
      <BoardNameDialog />
      <ConfirmDialog />
      <AppearanceDialog />
      <BoardSwitcherDialog />
      <ProfileDialog />
      <AccountSettingsDialog />
      <ShareBoardDialog />
      <ProjectDialogs />
    </MatchSetContext.Provider>
  )
}

function BoardArea({ phase }: { phase: string }) {
  const boards = useBoardStore((state) => state.boards)
  const viewedVersion = useBoardStore((state) => state.viewedVersion)
  const setBoardNameDialog = useUiStore((state) => state.setBoardNameDialog)
  const initialise = useBoardStore((state) => state.initialise)

  if (phase === 'loading' || phase === 'idle') {
    return (
      <section className={styles.state} aria-busy="true">
        <h2 className={styles.stateTitle}>Loading board…</h2>
      </section>
    )
  }

  if (phase === 'error') {
    return (
      <section className={styles.state}>
        <h2 className={styles.stateTitle}>The board could not be loaded</h2>
        <p className={styles.stateText}>Try again, or pick a different board from the list.</p>
        <Button variant="primary" onClick={() => void initialise()}>
          Try again
        </Button>
      </section>
    )
  }

  if (phase === 'missing') {
    return (
      <section className={styles.state}>
        <h2 className={styles.stateTitle}>That board no longer exists</h2>
        <p className={styles.stateText}>It may have been deleted. Choose another board or create a new one.</p>
        <Button variant="primary" onClick={() => setBoardNameDialog('create')}>
          New board
        </Button>
      </section>
    )
  }

  if (boards.length === 0) {
    return (
      <section className={styles.state}>
        <h2 className={styles.stateTitle}>No boards yet</h2>
        <p className={styles.stateText}>Create a board, or import a legacy export.</p>
        <Button variant="primary" onClick={() => setBoardNameDialog('create')}>
          New board
        </Button>
      </section>
    )
  }

  return viewedVersion
    ? <HistoricalBoardView revision={viewedVersion.revision} snapshot={viewedVersion.snapshot} />
    : <StoryMap />
}
